import { resetStores } from "@/store";
import { useProjectsStore } from "@/store/modules/projects";
import { useContributorsStore } from "@/store/modules/contributors";
import { useNotificationsStore } from "@/store/modules/socio-store/notification";
import ProjectChildren from "@/router/atmo/projects/project/children";
import { i18nGlobal as i18n } from "@/setup/i18n";
import { restoreListQuery } from "@/utils/listQueryMemory";
import { logRequestFailure } from "@/utils/network";
import { useNetworkStore } from "@/composables/network/useNetwork";

const ProjectsList = () =>
  import(
    /* webpackChunkName: "ProjectsList" */ "@/components/atmo/projects/ProjectsList.vue"
  );

const Project = () =>
  import(
    /* webpackChuckName "Project" */ "@/components/atmo/projects/Project.vue"
  );

const messages = {
  fr: {
    errorProjectFetch:
      "Une erreur est survenue lors de la récupération du projet",
    projectNoLongerExists:
      "Cette mission n'existe plus, retour à la liste des missions",
    errorContributorsFetch:
      "Les intervenants n'ont pas pu être chargés, la mission reste en lecture seule",
    offlineMissionMissing:
      "Les données de la mission préparée ne sont plus sur cet appareil : le mode hors ligne est désactivé.",
  },
  en: {
    errorProjectFetch: "An error occurred while fetching the project",
    errorContributorsFetch:
      "Contributors could not be loaded, the mission stays read-only",
    projectNoLongerExists:
      "This mission no longer exists, back to the missions list",
    offlineMissionMissing:
      "The prepared mission's data is no longer on this device: offline mode is turned off.",
  },
};

i18n.mergeLocaleMessage("fr", messages.fr);
i18n.mergeLocaleMessage("en", messages.en);

const isNotFound = (error) => error?.response?.status === 404;

const dropDeletedProject = async (projectId) => {
  await useProjectsStore().dropLocalProject(projectId);
  useNotificationsStore().showWarningNotification(
    i18n.t("projectNoLongerExists")
  );
};

export const fetchProject = async (to, from, next) => {
  if (to.params.projectId === from.params.projectId) return next();

  const projectId = to.params.projectId;
  const projectsStore = useProjectsStore();
  resetStores();

  let hasLocalProject = false;
  try {
    await projectsStore.setCurrentProject(projectId);
    hasLocalProject = true;
  } catch {
    hasLocalProject = false;
  }

  const networkStore = useNetworkStore();
  if (!hasLocalProject && networkStore.isOfflineMode) {
    networkStore.setOfflineMode(false);
    useNotificationsStore().showWarningNotification(
      i18n.t("offlineMissionMissing")
    );
    return next({ name: "projects" });
  }

  if (!hasLocalProject) {
    try {
      await projectsStore.syncFullProject(projectId);
      await projectsStore.setCurrentProject(projectId);
    } catch (error) {
      if (isNotFound(error)) {
        await dropDeletedProject(projectId);
        return next({ name: "projects" });
      }
      useNotificationsStore().showErrorNotification(
        i18n.t("errorProjectFetch")
      );
      return next({ name: "not-found" });
    }
  }

  next();

  useContributorsStore()
    .setUserCanChange(projectId)
    .catch((error) => {
      // L'échec laisse isCurrentUserCanChange à false : sans message, la
      // mission passe en lecture seule sans que personne ne sache pourquoi.
      useNotificationsStore().showWarningNotification(
        i18n.t("errorContributorsFetch")
      );
      console.error("Contributors prefetch failed", error);
    });
  if (hasLocalProject) {
    projectsStore
      .syncFullProject(projectId)
      .then(() => projectsStore.setCurrentProject(projectId))
      .catch(async (error) => {
        if (isNotFound(error)) {
          await dropDeletedProject(projectId);
          const { default: router } = await import("@/router");
          return router.replace({ name: "projects" });
        }
        logRequestFailure("Project background sync failed", error);
      });
  }
};

export default [
  {
    path: "",
    name: "projects",
    component: ProjectsList,
    beforeEnter: restoreListQuery("projects"),
  },
  {
    path: ":projectId",
    component: Project,
    children: ProjectChildren,
    beforeEnter: fetchProject,
  },
];
